import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  DocumentChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { boardReportApi } from '../lib/api';
import FeatureGate from '../components/FeatureGate';
import { StatusBadge, EmptyState, LoadingSpinner } from '../components/ui';
import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { useTeamId } from '../hooks/useTeamId';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatPeriod(period: string) {
  const labels: Record<string, string> = {
    MONTHLY: 'Monthly',
    QUARTERLY: 'Quarterly',
    ANNUAL: 'Annual',
    CUSTOM: 'Custom',
  };
  return labels[period] || period;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function BoardReportDetailPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Get user's team
  const { teamId } = useTeamId();

  // Fetch report
  const { data: report, isLoading, error } = useQuery({
    queryKey: ['board-report', teamId, id],
    queryFn: async () => {
      const res = await boardReportApi.get(teamId!, id!);
      return res.data;
    },
    enabled: !!teamId && !!id,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.status === 'GENERATING') return 5000;
      return false;
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => boardReportApi.delete(teamId!, id!),
    onSuccess: () => {
      toast.success('Report deleted');
      queryClient.invalidateQueries({ queryKey: ['board-reports'] });
      navigate('/board-reports');
    },
    onError: () => toast.error('Failed to delete report'),
  });

  // Download PDF
  const handleDownload = async () => {
    if (!report) return;
    try {
      const res = await boardReportApi.downloadPdf(teamId!, id!);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.title.replace(/\s+/g, '-').toLowerCase()}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  if (isLoading) return <LoadingSpinner size="lg" className="py-20" />;

  if (error || !report) {
    return (
      <div className="space-y-4">
        <Link
          to="/board-reports"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Board Reports
        </Link>
        <EmptyState
          icon={DocumentChartBarIcon}
          title="Report not found"
          description="This board report could not be loaded."
        />
      </div>
    );
  }

  const passRate = report.totalScans > 0
    ? Math.round((report.passedScans / report.totalScans) * 100)
    : 0;

  return (
    <FeatureGate feature="board_reports" mode="lock">
      <div className="space-y-6">
        {/* Back link + header */}
        <div>
          <Link
            to="/board-reports"
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to Board Reports
          </Link>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-ocean-50 flex-shrink-0 mt-0.5">
                <DocumentChartBarIcon className="h-5 w-5 text-ocean-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {report.title}
                </h1>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  {report.status === 'GENERATING' ? (
                    <StatusBadge variant="warning">
                      <svg className="animate-spin -ml-0.5 mr-1.5 h-3 w-3" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Generating
                    </StatusBadge>
                  ) : report.status === 'READY' ? (
                    <StatusBadge variant="success">Ready</StatusBadge>
                  ) : (
                    <StatusBadge variant="danger">Error</StatusBadge>
                  )}
                  <span className="text-sm text-gray-500">
                    {formatPeriod(report.period)}
                  </span>
                  <span className="text-gray-300">|</span>
                  <span className="text-sm text-gray-500">
                    {formatDate(report.periodStart)} — {formatDate(report.periodEnd)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {report.status === 'READY' && (
                <button
                  onClick={handleDownload}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-ocean-600 px-3 py-2 text-sm font-medium text-white hover:bg-ocean-700"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  Download PDF
                </button>
              )}
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
              >
                <TrashIcon className="h-4 w-4" />
                Delete
              </button>
            </div>
          </div>
        </div>

        {/* Status-specific content */}
        {report.status === 'GENERATING' && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
            <svg className="animate-spin mx-auto h-8 w-8 text-amber-500 mb-3" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <p className="text-sm font-medium text-amber-800">
              Your board report is being generated. This page will update automatically.
            </p>
          </div>
        )}

        {report.status === 'ERROR' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center gap-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800">Report generation failed</p>
                {report.errorMessage && (
                  <p className="mt-1 text-sm text-red-600">{report.errorMessage}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {report.status === 'READY' && (
          <>
            {/* Scan statistics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="text-sm text-gray-500">Total Scans</div>
                <div className="mt-1 text-2xl font-bold text-gray-900">{report.totalScans}</div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-1 text-sm text-green-600">
                  <CheckCircleIcon className="h-4 w-4" />
                  Passed
                </div>
                <div className="mt-1 text-2xl font-bold text-gray-900">{report.passedScans}</div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-1 text-sm text-red-600">
                  <XCircleIcon className="h-4 w-4" />
                  Failed
                </div>
                <div className="mt-1 text-2xl font-bold text-gray-900">{report.failedScans}</div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="text-sm text-gray-500">Pass Rate</div>
                <div className="mt-1 text-2xl font-bold text-gray-900">{passRate}%</div>
              </div>
            </div>

            {/* Average UDAAP Risk */}
            {report.avgUdaapRisk != null && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
                <h2 className="text-base font-semibold text-gray-900 mb-2">
                  Average UDAAP Risk Score
                </h2>
                <div className="flex items-center gap-4">
                  <div className="text-3xl font-bold text-gray-900">
                    {report.avgUdaapRisk.toFixed(1)}
                  </div>
                  <div className="flex-1">
                    <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          report.avgUdaapRisk <= 3 ? 'bg-green-500' :
                          report.avgUdaapRisk <= 6 ? 'bg-amber-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(report.avgUdaapRisk * 10, 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>Low Risk</span>
                      <span>High Risk</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Top Violations */}
            {report.topViolations && Array.isArray(report.topViolations) && report.topViolations.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-base font-semibold text-gray-900">
                    Top Violations
                  </h2>
                </div>
                <div className="divide-y divide-gray-200">
                  {report.topViolations.map((violation: any, idx: number) => (
                    <div key={idx} className="px-6 py-3 flex items-center justify-between">
                      <span className="text-sm text-gray-900">
                        {violation.rule || violation.name || violation.type || JSON.stringify(violation)}
                      </span>
                      {violation.count != null && (
                        <span className="text-sm font-medium text-red-600">
                          {violation.count} occurrences
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Report metadata */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">
              Report Details
            </h2>
          </div>
          <div className="px-6 py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">Period</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatPeriod(report.period)}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">Date Range</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {formatDate(report.periodStart)} — {formatDate(report.periodEnd)}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase">Created</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatDate(report.createdAt)}</dd>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} className="relative z-50">
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                Delete Report
              </Dialog.Title>
              <p className="mt-2 text-sm text-gray-500">
                Are you sure you want to delete this board report? This action cannot be undone.
              </p>
              <div className="mt-4 flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteMutation.mutate()}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      </div>
    </FeatureGate>
  );
}
